import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyboardIcon, MicIcon } from 'lucide-react';
import { ThreadPulses } from './ThreadPulses';
import { BeheerMenu } from './BeheerMenu';
import { NoodstopButton } from '../ui/NoodstopButton';
import { RollingCounter } from '../ui/RollingCounter';
import type { AnnaStage } from '../../hooks/useAnnaStage';
interface WallBottomBarProps {
  stage: AnnaStage;
  listening: boolean;
  calm: boolean;
  /** Narrow screens have no satellites, so the agents show as pulses here */
  showThreads: boolean;
  onMic: () => void;
  onType: () => void;
}
const fade = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    duration: 0.3
  }
};

/** Bottom edge, very quiet. Mic and Noodstop are always reachable, even when the wall is calm or asleep. */
export function WallBottomBar({
  stage,
  listening,
  calm,
  showThreads,
  onMic,
  onType
}: WallBottomBarProps) {
  const sleep = stage.view === 'sleep';
  const minimal = sleep || calm;
  /** "Wacht op jou" and agent pulses stay hidden until the house wakes. */
  const houseAsleep = stage.mode === 'Slapen';
  const waiting = stage.pending.length;
  return <footer className="pointer-events-none absolute inset-x-0 bottom-0 z-20 grid grid-cols-1 items-end gap-5 px-6 pb-6 md:grid-cols-[1fr_auto_1fr] lg:px-10 lg:pb-8" data-id="element-1745" data-mp="y2hemlw363l">
      <div className="pointer-events-auto hidden min-w-0 md:block" data-id="element-1746" data-mp="3s20yy15htofr">
        <AnimatePresence data-id="element-1747" data-mp="34esqxbgok1a1">
          {showThreads && !minimal && !houseAsleep && <motion.div {...fade} data-id="element-1748" data-mp="30dz4kmivyuj9">
              <ThreadPulses stopped={stage.stopped} flash={stage.flash} data-id="element-1749" data-mp="30al3ripvetdx" />
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="pointer-events-auto flex items-center justify-center gap-3" data-id="element-1750" data-mp="39d40rm6z6bs9">
        <AnimatePresence data-id="element-1751" data-mp="2lni1kyqlrwk9">
          {!minimal && !houseAsleep && <motion.button {...fade} type="button" onClick={stage.view === 'approvals' ? stage.leaveApprovals : stage.openApprovals} disabled={waiting === 0 && stage.view !== 'approvals'} className="flex h-14 items-center gap-3 whitespace-nowrap rounded-full border border-white/10 bg-night-900 pl-5 pr-2 text-[22px] text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink disabled:opacity-50" data-id="element-1752" data-mp="2llf45tn4f15i">
              {stage.view === 'approvals' ? 'Terug naar Anna' : 'Wacht op jou'}
              <span className="grid h-10 min-w-[40px] place-items-center rounded-full bg-white/[0.08] px-3 text-[22px] font-semibold text-ink" data-id="element-1753" data-mp="1oira4qqpn5l">
                <RollingCounter value={waiting} data-id="element-1754" data-mp="bkceci234z1m" />
              </span>
            </motion.button>}
        </AnimatePresence>
        <button type="button" onClick={onMic} aria-pressed={listening} aria-label={listening ? 'Stop met luisteren' : 'Praat met Anna'} className={`grid h-16 w-16 place-items-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${listening ? 'bg-accent text-night-950' : 'border border-white/15 bg-night-900 text-ink hover:border-accent/60'}`} data-id="element-1755" data-mp="fn8odkdk7mty">
          <MicIcon className="h-7 w-7" strokeWidth={1.75} data-id="element-1756" data-mp="32q1ujlzpyo1u" />
        </button>
        {!minimal && <button type="button" onClick={onType} aria-label="Typ een vraag aan Anna" className="grid h-14 w-14 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1757" data-mp="2xrqntk4q0tnu">
            <KeyboardIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-1758" data-mp="2jtim6ywqp46c" />
          </button>}
      </div>

      <div className="pointer-events-auto flex items-center justify-center gap-4 md:justify-end" data-id="element-1759" data-mp="2eupejn44w7e3">
        <AnimatePresence data-id="element-1760" data-mp="hzt387s4x62i">
          {!minimal && <motion.div {...fade} data-id="element-1761" data-mp="d2aou1vclyn4">
              <BeheerMenu data-id="element-1762" data-mp="d32iv89aroba" />
            </motion.div>}
        </AnimatePresence>
        <NoodstopButton size="wall" onTrigger={stage.emergencyStop} data-id="element-1763" data-mp="35w9lgh4ayfg4" />
      </div>
    </footer>;
}